// This is a dynamic form element renderer, based on these types of responses
// type: M (multi), D (drop down), T (text)
// Then for M, you have disc_values showing value options
// For D, same sort of thing
// Lastly, for T, you may see disc_texts single element array, identifying
// the textual constraints - A = alpha, with min+max bounds
//                           D = date
// TODO: There is also an alphanumeric type to show
// See: Giant JSON sample at bottom of this file if needing a struct reference
import * as React from 'react'
import { Select } from '@paytrace/pt-skel/Component'

import { PatternGuardedInputText } from './PatternGuardedInputText'

import * as T from '../types'

export interface FormGroupProps extends React.ComponentProps<any> {
  hintText?: React.ReactNode,
}

// These are the native html input attributes, including event handlers
type InputDOMProps = React.InputHTMLAttributes<HTMLInputElement>
  & React.ClassAttributes<HTMLInputElement>
  & FormGroupProps

export interface PropsWithDD extends InputDOMProps {
  dd_props: T.DDPayload
}

export function DDMultiSelect (
  props: {
    value?: any,
    className: string,
    label: string,
    values: string[]
  }
) {
  const { label, values, ...passProps } = props
  let { value } = props
  const options = values.map(val => ({ name: val, val }))

  // Need to ensure this is always an array, including on init
  if (!value) {
    value = []
  }

  if (!Array.isArray(value)) {
    value = [value]
  }

  // TODO: Curiously this *ONLY* works with passProps...why?
  // Must be missing something compared to when trying to explicitly map
  return (
    <Select
      multiple
      label={label}
      options={options}
      hintText='Please select one or more options'
      {...passProps}
      value={value} />
  )
}

export function DDSelect (
  props: { className: string, label: string, values: string[] }
) {
  const { label, values, ...passProps } = props
  const options = [{ name: 'Select an option', val: '' }, ...values.map(val => ({ name: val, val }))]

  return (
    <Select
      label={label}
      options={options}
      hintText='Please select an option from the dropdown.'
      {...passProps} />
  )
}

export function DDText (
  props: {
    label: string,
    className: string,
    constraints: { data_type: string, max: string, min: string },
  } & PropsWithDD
) {
  const { constraints, label, onChange, value, required, className } = props
  let pattern: string = ''
  let hintText: string = 'Please provide a value.'

  switch (constraints.data_type.toUpperCase()) {
    case 'N': // Numeric only
      pattern = '^[0-9]*$'
      hintText = 'Please provide a numeric value without any special characters.'
      break
    case 'A': // Alpha
      pattern = '^[A-Za-z\\s~!@#$%^&*()_+=\\-`{}|";<>?/.\',:\\[\\]]*$'
      hintText = 'Please provide alphabetic characters. Numbers are not allowed, but special characters are permitted.'
      break
    case '#': // Alpha-Numeric
      pattern = '^.*$'
      hintText = 'Please provide a combination of alphabetic characters and numbers, special characters are also permitted.'
      break
    case 'D':
      pattern = '^[0-9-/]*$'
      hintText = 'Please provide a date value in the format YYYY-MM-DD.'
      break
    default:
      break
  }

  return (
    <PatternGuardedInputText
      name={label}
      label={label}
      minLength={Number(constraints.min)}
      maxLength={Number(constraints.max)}
      placeholder=''
      value={value}
      hintText={hintText}
      onChange={onChange}
      pattern={pattern}
      required={required}
      className={className}
    />
  )
}

export function DiscData (props: PropsWithDD) {
  const { dd_props, ...passProps } = props
  const required = 'Y' === dd_props.required.toUpperCase()
  const className = ('uid-dd-' + dd_props.disc_id) + ' ' + (props.className || '')

  if ('M' === dd_props.type) {
    return (
      <DDMultiSelect
        label={dd_props.title}
        values={dd_props.disc_values.map(v => v.value)}
        required={required}
        {...passProps}
        className={className}
      />
    )
  }

  if ('D' === dd_props.type) {
    return (
      <DDSelect
        label={dd_props.title}
        values={dd_props.disc_values.map(v => v.value)}
        required={required}
        {...passProps}
        className={className}
      />
    )
  }

  if ('T' === dd_props.type) {
    return (
      <DDText
        label={dd_props.title}
        constraints={dd_props.disc_texts[0]}
        required={required}
        {...props}
        className={className}
      />
    )
  }

  return <pre>{JSON.stringify(dd_props, null, 4)}</pre>
}
/* [
 *   {
 *     "type": "M",
 *     "disc_id": 20588,
 *     "disc_type": "C",
 *     "paytrace_id": "63161",
 *     "title": "Discretionary Data Multiple Select",
 *     "on_receipt": "Y",
 *     "required": "N",
 *     "disc_values": [
 *       {
 *         "value": "Multiple Select/Option 1",
 *         "disc_value_id": 12923,
 *         "disc_id": "20588"
 *       },
 *       {
 *         "value": "Multiple Select, Option 2",
 *         "disc_value_id": 12924,
 *         "disc_id": "20588"
 *       },
 *       {
 *         "value": "Multiple Select Option 3",
 *         "disc_value_id": 12925,
 *         "disc_id": "20588"
 *       }
 *     ],
 *     "disc_texts": []
 *   },
 *   {
 *     "type": "D",
 *     "disc_id": 20589,
 *     "disc_type": "C",
 *     "paytrace_id": "63161",
 *     "title": "Discretionary Data Drop Down",
 *     "on_receipt": "Y",
 *     "required": "N",
 *     "disc_values": [
 *       {
 *         "value": "Drop Down/Option 1",
 *         "disc_value_id": 12926,
 *         "disc_id": "20589"
 *       },
 *       {
 *         "value": "Drop Down,Option 2",
 *         "disc_value_id": 12927,
 *         "disc_id": "20589"
 *       },
 *       {
 *         "value": "Drop Down Option 3",
 *         "disc_value_id": 12928,
 *         "disc_id": "20589"
 *       }
 *     ],
 *     "disc_texts": []
 *   },
 *   {
 *     "type": "T",
 *     "disc_id": 20590,
 *     "disc_type": "C",
 *     "paytrace_id": "63161",
 *     "title": "Discretionary Data Text Alpha-Numeric 3-99",
 *     "on_receipt": "Y",
 *     "required": "Y",
 *     "disc_values": [],
 *     "disc_texts": [
 *       {
 *         "disc_id": "20590",
 *         "data_type": "#",
 *         "max": "99",
 *         "min": "3"
 *       }
 *     ]
 *   },
 *   {
 *     "type": "T",
 *     "disc_id": 20591,
 *     "disc_type": "C",
 *     "paytrace_id": "63161",
 *     "title": "Discretionary Data Text Numeric Only 7-7",
 *     "on_receipt": "Y",
 *     "required": "Y",
 *     "disc_values": [],
 *     "disc_texts": [
 *       {
 *         "disc_id": "20591",
 *         "data_type": "N",
 *         "max": "7",
 *         "min": "7"
 *       }
 *     ]
 *   },
 *   {
 *     "type": "T",
 *     "disc_id": 20592,
 *     "disc_type": "C",
 *     "paytrace_id": "63161",
 *     "title": "Discretionary Data Text Alpha Only 0-50",
 *     "on_receipt": "Y",
 *     "required": "Y",
 *     "disc_values": [],
 *     "disc_texts": [
 *       {
 *         "disc_id": "20592",
 *         "data_type": "A",
 *         "max": "50",
 *         "min": "0"
 *       }
 *     ]
 *   },
 *   {
 *     "type": "T",
 *     "disc_id": 20593,
 *     "disc_type": "C",
 *     "paytrace_id": "63161",
 *     "title": "Discretionary Data Text Date 0-10",
 *     "on_receipt": "Y",
 *     "required": "Y",
 *     "disc_values": [],
 *     "disc_texts": [
 *       {
 *         "disc_id": "20593",
 *         "data_type": "D",
 *         "max": "10",
 *         "min": "0"
 *       }
 *     ]
 *   }
 * ] */
